import React, { useState, useEffect } from 'react';
import { Filter, Calendar, BarChart3, X } from 'lucide-react';
import { UnimedCard, UnimedButton, UnimedBadge } from './ui/UnimedComponents';

const DataFilters = ({ data, onFilterChange, initialFilters = {} }) => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    sapsRange: { min: '', max: '' },
    outcomeFilter: 'all', // 'all', 'survivors', 'deceased'
    ...initialFilters
  });

  const [isExpanded, setIsExpanded] = useState(false);

  // Get date range from data
  const getDateRange = () => {
    if (!data || data.length === 0) return { min: '', max: '' };
    
    const dates = data
      .map(patient => {
        // Try different date fields that might exist
        return patient.dataAtendimento || patient.dataInternacao || patient.date || patient.admissionDate;
      })
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    
    return {
      min: dates[0] ? formatDateForInput(dates[0]) : '',
      max: dates[dates.length - 1] ? formatDateForInput(dates[dates.length - 1]) : ''
    };
  };

  // Get SAPS3 score range from data
  const getSapsRange = () => {
    if (!data || data.length === 0) return { min: 0, max: 217 };
    
    const scores = data.map(patient => patient.saps3Score).filter(score => score > 0);
    return {
      min: Math.min(...scores),
      max: Math.max(...scores)
    };
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Error formatting date:', date, error);
      return '';
    }
  };

  const parseInputDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString + 'T00:00:00');
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
      return null;
    }
  };

  // Apply filters to data
  const applyFilters = (currentFilters) => {
    if (!data) return [];

    return data.filter(patient => {
      // Date range filter
      const patientDate = patient.dataAtendimento || patient.dataInternacao || patient.date || patient.admissionDate;
      
      if (currentFilters.dateRange.start && patientDate) {
        const startDate = parseInputDate(currentFilters.dateRange.start);
        const pDate = new Date(patientDate);
        if (startDate && !isNaN(pDate.getTime()) && pDate < startDate) return false;
      }
      
      if (currentFilters.dateRange.end && patientDate) {
        const endDate = parseInputDate(currentFilters.dateRange.end);
        const pDate = new Date(patientDate);
        if (endDate && !isNaN(pDate.getTime())) {
          endDate.setHours(23, 59, 59); // Include the entire end date
          if (pDate > endDate) return false;
        }
      }

      // SAPS3 score range filter
      if (currentFilters.sapsRange.min !== '') {
        if (patient.saps3Score < parseFloat(currentFilters.sapsRange.min)) return false;
      }
      
      if (currentFilters.sapsRange.max !== '') {
        if (patient.saps3Score > parseFloat(currentFilters.sapsRange.max)) return false;
      }

      // Outcome filter
      if (currentFilters.outcomeFilter === 'survivors' && patient.deceased) return false;
      if (currentFilters.outcomeFilter === 'deceased' && !patient.deceased) return false;

      return true;
    });
  };

  // Update filters and notify parent
  const updateFilters = (newFilters) => {
    setFilters(newFilters);
    const filteredData = applyFilters(newFilters);
    onFilterChange(filteredData, newFilters);
  };

  // Handle filter input changes
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters };
    
    if (filterType.includes('.')) {
      const [parent, child] = filterType.split('.');
      newFilters[parent] = { ...newFilters[parent], [child]: value };
    } else {
      newFilters[filterType] = value;
    }
    
    updateFilters(newFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      dateRange: { start: '', end: '' },
      sapsRange: { min: '', max: '' },
      outcomeFilter: 'all'
    };
    updateFilters(clearedFilters);
  };

  // Initialize filters on data load
  useEffect(() => {
    if (data && data.length > 0) {
      const filteredData = applyFilters(filters);
      onFilterChange(filteredData, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data || data.length === 0) return null;

  const dateRange = getDateRange();
  const sapsRange = getSapsRange();
  const hasActiveFilters = filters.dateRange.start || filters.dateRange.end || 
                          filters.sapsRange.min !== '' || filters.sapsRange.max !== '' || 
                          filters.outcomeFilter !== 'all';

  return (
    <UnimedCard className="mb-6">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="bg-gradient-to-br from-unimed-green-500 to-unimed-green-600 p-2 rounded-lg mr-3">
              <Filter className="h-5 w-5 text-white" />
            </div>
            <h3 className="unimed-titulo-principal text-xl text-unimed-darkgreen-800">FILTROS DE DADOS</h3>
            {hasActiveFilters && (
              <UnimedBadge variant="success" className="ml-3">
                ATIVO
              </UnimedBadge>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {hasActiveFilters && (
              <UnimedButton
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-unimed-orange-600 border-unimed-orange-300 hover:bg-unimed-orange-50"
              >
                <X className="h-4 w-4 mr-1" />
                LIMPAR
              </UnimedButton>
            )}
            <UnimedButton
              variant="secondary"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'MENOS' : 'MAIS'} FILTROS
            </UnimedButton>
          </div>
        </div>

        <div className={`grid gap-6 ${isExpanded ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}>
          {/* Date Range Filter */}
          <div className="space-y-3">
            <label className="flex items-center unimed-chapeu text-sm font-semibold text-unimed-darkgreen-700">
              <Calendar className="h-4 w-4 mr-2 text-unimed-green-600" />
              PERÍODO
            </label>
            <div className="space-y-2">
              <input
                type="date"
                min={dateRange.min}
                max={dateRange.max}
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                className="w-full px-3 py-2 border-2 border-unimed-beige-300 rounded-lg text-sm unimed-texto-corrido focus:outline-none focus:ring-2 focus:ring-unimed-green-500 focus:border-unimed-green-500 transition-colors"
                placeholder="Data inicial"
              />
              <input
                type="date"
                min={filters.dateRange.start || dateRange.min}
                max={dateRange.max}
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                className="w-full px-3 py-2 border-2 border-unimed-beige-300 rounded-lg text-sm unimed-texto-corrido focus:outline-none focus:ring-2 focus:ring-unimed-green-500 focus:border-unimed-green-500 transition-colors"
                placeholder="Data final"
              />
            </div>
          </div>

          {/* SAPS3 Score Range */}
          <div className="space-y-3">
            <label className="flex items-center unimed-chapeu text-sm font-semibold text-unimed-darkgreen-700">
              <BarChart3 className="h-4 w-4 mr-2 text-unimed-orange-600" />
              SCORE SAPS3
            </label>
            <div className="space-y-2">
              <input
                type="number"
                min={sapsRange.min}
                max={sapsRange.max}
                value={filters.sapsRange.min}
                onChange={(e) => handleFilterChange('sapsRange.min', e.target.value)}
                className="w-full px-3 py-2 border-2 border-unimed-beige-300 rounded-lg text-sm unimed-texto-corrido focus:outline-none focus:ring-2 focus:ring-unimed-orange-500 focus:border-unimed-orange-500 transition-colors"
                placeholder={`Min (${sapsRange.min})`}
              />
              <input
                type="number"
                min={filters.sapsRange.min || sapsRange.min}
                max={sapsRange.max}
                value={filters.sapsRange.max}
                onChange={(e) => handleFilterChange('sapsRange.max', e.target.value)}
                className="w-full px-3 py-2 border-2 border-unimed-beige-300 rounded-lg text-sm unimed-texto-corrido focus:outline-none focus:ring-2 focus:ring-unimed-orange-500 focus:border-unimed-orange-500 transition-colors"
                placeholder={`Max (${sapsRange.max})`}
              />
            </div>
          </div>

          {/* Outcome Filter */}
          <div className="space-y-3">
            <label className="unimed-chapeu text-sm font-semibold text-unimed-darkgreen-700">DESFECHO</label>
            <select
              value={filters.outcomeFilter}
              onChange={(e) => handleFilterChange('outcomeFilter', e.target.value)}
              className="w-full px-3 py-2 border-2 border-unimed-beige-300 rounded-lg text-sm unimed-texto-corrido focus:outline-none focus:ring-2 focus:ring-unimed-lightblue-500 focus:border-unimed-lightblue-500 transition-colors bg-white"
            >
              <option value="all">TODOS</option>
              <option value="survivors">SOBREVIVENTES</option>
              <option value="deceased">ÓBITOS</option>
            </select>
          </div>

          {/* Additional filters when expanded */}
          {isExpanded && (
            <div className="space-y-3">
              <label className="unimed-chapeu text-sm font-semibold text-unimed-darkgreen-700">FILTROS RÁPIDOS</label>
              <div className="flex flex-wrap gap-2">
                <UnimedButton
                  size="sm"
                  variant="outline"
                  onClick={() => updateFilters({
                    ...filters,
                    sapsRange: { min: '0', max: '45' }
                  })}
                  className="text-unimed-green-700 border-unimed-green-300 bg-unimed-green-50 hover:bg-unimed-green-100"
                >
                  BAIXO RISCO (≤≠45)
                </UnimedButton>
                <UnimedButton
                  size="sm"
                  variant="outline"
                  onClick={() => updateFilters({
                    ...filters,
                    sapsRange: { min: '46', max: '75' }
                  })}
                  className="text-unimed-orange-700 border-unimed-orange-300 bg-unimed-orange-50 hover:bg-unimed-orange-100"
                >
                  MÉDIO RISCO (46-75)
                </UnimedButton>
                <UnimedButton
                  size="sm"
                  variant="outline"
                  onClick={() => updateFilters({
                    ...filters,
                    sapsRange: { min: '76', max: '217' }
                  })}
                  className="text-red-700 border-red-300 bg-red-50 hover:bg-red-100"
                >
                  ALTO RISCO (≥≠76)
                </UnimedButton>
              </div>
            </div>
          )}
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="mt-6 p-4 bg-unimed-beige-50 rounded-xl border border-unimed-beige-200">
            <p className="unimed-titulo-secundario font-medium text-unimed-darkgreen-800 mb-3">FILTROS APLICADOS:</p>
            <div className="flex flex-wrap gap-2">
              {filters.dateRange.start && (
                <UnimedBadge variant="info">
                  INÍCIO: {new Date(filters.dateRange.start).toLocaleDateString('pt-BR')}
                </UnimedBadge>
              )}
              {filters.dateRange.end && (
                <UnimedBadge variant="info">
                  FIM: {new Date(filters.dateRange.end).toLocaleDateString('pt-BR')}
                </UnimedBadge>
              )}
              {filters.sapsRange.min !== '' && (
                <UnimedBadge variant="warning">
                  SAPS3 ≥ {filters.sapsRange.min}
                </UnimedBadge>
              )}
              {filters.sapsRange.max !== '' && (
                <UnimedBadge variant="warning">
                  SAPS3 ≤ {filters.sapsRange.max}
                </UnimedBadge>
              )}
              {filters.outcomeFilter !== 'all' && (
                <UnimedBadge variant="success">
                  {filters.outcomeFilter === 'survivors' ? 'SOBREVIVENTES' : 'ÓBITOS'}
                </UnimedBadge>
              )}
            </div>
          </div>
        )}
      </div>
    </UnimedCard>
  );
};

export default DataFilters;
